import { KEYBINDS } from '../../config/constants.js';

// The same class ui/menuKeyNav.js paints its highlight with, so the pick
// screen's selected card looks identical to a selected menu button rather
// than inventing a second "this one is highlighted" visual language.
const SELECTED_CLASS = 'menu-button-selected';

/**
 * DOM overlay for the level-up pick screen. Pure presentation — all it
 * does is render whatever choices ProgressionSystem hands it and forward
 * the click back via `resolvePick`. Mounted into #ui-root alongside any
 * other overlay (MainMenu, ShopMenu — M5).
 *
 * **Keyboard.** Three ways in, because this is the screen the player hits
 * most often in a run and it used to accept only two of them:
 *
 *  - `1`/`2`/`3`/`4` (KEYBINDS.levelUpPick) pick that card outright. Fast,
 *    but it was *invisible* — nothing on the card said the shortcut existed,
 *    so it only helped players who had read the source. The cards now carry
 *    the index badge that makes it discoverable.
 *  - A/D or ←/→ move a highlight, Enter/Space confirms it. This is what
 *    every home menu does (ui/menuKeyNav.js), and its absence here was the
 *    single worst input inconsistency in the game: the level-up screen
 *    swallowed Enter, so a player navigating by keyboard hit a wall on the
 *    one screen they cannot skip.
 *  - Clicking, as before.
 *
 * This deliberately does NOT go through menuKeyNav. That module drives
 * `.menu-overlay` screens and scopes itself to the topmost one; the pick
 * screen is a mid-run modal that can be stacked over a menu overlay, and
 * routing it through the shared navigator would let a direction key reach
 * whatever is underneath. The nav here is a dozen lines and owns exactly
 * one row of cards.
 *
 * W/S are deliberately not bound: the cards are a single row, and the sim
 * is paused (Game listens for `xp:levelup:open`), so the player's hand is
 * still on WASD — binding the vertical axis to nothing keeps a reflexive
 * W-press from doing something surprising.
 */
const NAV_PREV = ['KeyA', 'ArrowLeft'];
const NAV_NEXT = ['KeyD', 'ArrowRight'];
const NAV_CONFIRM = ['Enter', 'Space', 'NumpadEnter'];

export class LevelUpPicker {
  constructor(eventBus, progression, root) {
    this.eventBus = eventBus;
    this.progression = progression;
    this.root = root;
    this.el = null;
    this._choices = [];
    this._cards = [];
    this._selected = 0;

    this._onKeyDown = (e) => {
      const index = KEYBINDS.levelUpPick.indexOf(e.code);
      if (index !== -1) {
        const choice = this._choices[index];
        if (choice) this._pick(choice.id);
        return;
      }
      if (this._choices.length === 0) return;

      if (NAV_PREV.includes(e.code)) {
        e.preventDefault();
        this._select(this._selected - 1);
      } else if (NAV_NEXT.includes(e.code)) {
        e.preventDefault();
        this._select(this._selected + 1);
      } else if (NAV_CONFIRM.includes(e.code)) {
        // Space is also the ultimate key. Swallowing it here is correct —
        // the sim is paused and there is nothing to fire at — but it must
        // not also scroll the page.
        e.preventDefault();
        const choice = this._choices[this._selected];
        if (choice) this._pick(choice.id);
      }
    };

    eventBus.on('xp:levelup:open', ({ level, choices }) => this._show(level, choices));
    // A run can end (or ProgressionSystem.forceResolvePending can silently
    // auto-apply a pick) while a pick screen is still up — e.g. a fatal hit
    // and an XP-threshold crossing landing in the same tick, or the run
    // ending right on a boss kill that also granted a level. Both leave the
    // sim already moved on with no further input coming, so tear the DOM
    // down here. NOT tied to boss:died itself — most boss kills now grant a
    // guaranteed level-up (see ProgressionSystem's boss:died handler) that
    // the player is meant to actually pick, so the picker has to survive a
    // plain boss:died and only close via the player's own choice.
    eventBus.on('run:ended', () => this._hide());
    eventBus.on('xp:levelup:auto-resolved', () => this._hide());
  }

  _hide() {
    this.el?.remove();
    this.el = null;
    this._choices = [];
    this._cards = [];
    window.removeEventListener('keydown', this._onKeyDown);
  }

  /**
   * Move the highlight. Clamped rather than wrapped: with two or three cards
   * side by side, wrapping means a single D press can jump the selection
   * across the whole screen, and the player is reading these under time
   * pressure they've only just been released from.
   */
  _select(index) {
    const clamped = Math.max(0, Math.min(this._cards.length - 1, index));
    if (clamped === this._selected && this._cards[clamped]?.classList.contains(SELECTED_CLASS)) return;
    this._cards[this._selected]?.classList.remove(SELECTED_CLASS);
    this._selected = clamped;
    this._cards[clamped]?.classList.add(SELECTED_CLASS);
  }

  _show(level, choices) {
    // Defensive: normally the previous overlay is already gone (a chained
    // level-up arrives via _pick, which captures and removes it). But now
    // that a keyboard highlight is stateful, two overlays coexisting for even
    // one frame would leave a lit card the arrow keys no longer control, so
    // the "one pick screen at a time" invariant is asserted here rather than
    // assumed from the caller.
    this.el?.remove();
    this._choices = choices;
    this._cards = [];
    this._selected = 0;
    window.addEventListener('keydown', this._onKeyDown);

    this.el = document.createElement('div');
    this.el.className = 'levelup-overlay';

    const title = document.createElement('h2');
    title.className = 'levelup-title';
    title.textContent = `Level ${level}`;
    this.el.appendChild(title);
    const subtitle = document.createElement('div');
    subtitle.className = 'levelup-subtitle';
    subtitle.textContent = 'Choose one upgrade';
    this.el.appendChild(subtitle);

    const cards = document.createElement('div');
    cards.className = 'levelup-cards';
    for (const [index, choice] of choices.entries()) {
      const card = document.createElement('button');
      card.className = `levelup-card levelup-card-${choice.rarity ?? 'common'}`;
      card.type = 'button';

      // The number key that picks this card. The shortcut has existed since
      // KEYBINDS.levelUpPick was written and nothing ever said so — a
      // shortcut nobody can see is a shortcut nobody uses.
      const badge = document.createElement('div');
      badge.className = 'levelup-card-index';
      badge.textContent = String(index + 1);
      badge.setAttribute('aria-hidden', 'true');
      card.appendChild(badge);

      const rarity = document.createElement('div');
      rarity.className = 'levelup-card-rarity';
      rarity.textContent = choice.rarity === 'pact'
        ? '⟁ PACT'
        : choice.rarity === 'fusion'
          ? '✧ FUSION'
          : `${choice.rarity === 'rare' ? '✦' : choice.rarity === 'uncommon' ? '◆' : '·'} ${(choice.rarity ?? 'common').toUpperCase()}`;
      card.appendChild(rarity);

      const label = document.createElement('div');
      label.className = 'levelup-card-label';
      label.textContent = choice.label;
      card.appendChild(label);

      if (choice.kind === 'pact') {
        // Boon-over-cost split instead of one description line — a pact has
        // to read as a bargain at a glance, not a paragraph.
        const boon = document.createElement('div');
        boon.className = 'levelup-card-desc levelup-card-boon';
        boon.textContent = choice.boonText;
        card.appendChild(boon);

        const cost = document.createElement('div');
        cost.className = 'levelup-card-desc levelup-card-cost';
        cost.textContent = choice.costText;
        card.appendChild(cost);
      } else {
        const desc = document.createElement('div');
        desc.className = 'levelup-card-desc';
        desc.textContent = choice.description;
        card.appendChild(desc);
      }

      // The Harmonics chase (BULLET-HEAVEN.md §H4), stated where the choice
      // is actually made. Without it a player maxes two weapons that happen
      // not to be a recipe pair and never learns fusions existed — see
      // ProgressionSystem._fusionHintFor for the bench numbers.
      if (choice.fusionHint) {
        const hint = document.createElement('div');
        hint.className = 'levelup-card-fusion-hint';
        hint.textContent = choice.fusionHint;
        card.appendChild(hint);
      }

      card.addEventListener('click', () => this._pick(choice.id));
      // Hovering moves the same single highlight the keys drive, so exactly
      // one card is ever lit no matter how mouse and keyboard are mixed —
      // the rule menuKeyNav established for every other screen.
      card.addEventListener('mouseenter', () => this._select(index));
      this._cards.push(card);
      cards.appendChild(card);
    }
    this.el.appendChild(cards);

    const hint = document.createElement('div');
    hint.className = 'levelup-hint';
    hint.textContent = '1–' + choices.length + ' or ←/→ · ENTER to confirm';
    this.el.appendChild(hint);

    this.root.appendChild(this.el);
    this._select(0);
  }

  _pick(choiceId) {
    // Capture before resolvePick(), which may synchronously chain straight
    // into the next pending level-up (multiple levels gained in one tick)
    // and reassign `this.el` to the new overlay before we get to remove it.
    const staleEl = this.el;
    // Torn down unconditionally up front, not just in _hide() — if
    // resolvePick() chains into another _show(), that re-adds the listener
    // and repopulates _choices for the new cards. If it doesn't chain, this
    // is the only place that ever un-registers the listener after a normal
    // (non run-ending) resolution, so leaving it out would leak the
    // listener and leave _choices pointing at stale, already-applied
    // choices for any later 1/2/3 press to misfire against.
    window.removeEventListener('keydown', this._onKeyDown);
    this._choices = [];
    this._cards = [];
    this.progression.resolvePick(choiceId);
    staleEl?.remove();
  }
}
